/**
 * useTriageStore — Zustand store for address triage data.
 *
 * Extracted from Workbench.tsx as part of Phase 0 full.
 * Owns: triageData, triageLoading, triageError + loadTriage action.
 *
 * The auto-triage *effect* stays in Workbench (needs effectiveChain, effectiveAddress,
 * loading which are Workbench-local), but calls store actions.
 *
 * Derived values (triageRiskScore, displayedRiskScore, etc.) are computed via
 * helper functions that read from both this store and useSelectionStore.
 */

import { create } from 'zustand';
import { addressService } from '../services/api';
import { toast } from 'sonner';
import type { Extensible } from '../types/graph';
import { toErrorMessage } from '../utils/safeStr';

// ── Types ────────────────────────────────────────────────────────────

export interface TriagePayload extends Extensible {
    address: string;
    chain: string;
    summary?: {
        risk_score?: number;
        risk_level?: string;
        entity_name?: string;
        entity_type?: string;
        subcategory?: string;
        sanctioned?: boolean;
        sanctions_entity?: string;
        nearest_exchange?: {
            name?: string;
            hops?: number;
        } | null;
        p2p_hits?: number;
        p2p_merchant?: boolean;
        telegram_mentions?: number;
        instagram_hits?: number;
        watchlist_hits?: number;
        tx_count?: number;
        // Auto-flag fields (Step 9b/9j, May 2026). Computed at the
        // backend triage chokepoint when risk_score >= 0.7, sanctioned,
        // or mixer_entity. Read by NodeIdentityChips for the flag chip,
        // ActionsTab for the Flag button state, and Workbench's triage
        // handler for canvas-node tag mirroring.
        auto_flagged?: boolean;
        flag_reasons?: string[];
        // Exchange-typing (Item 2). wallet_role = function of an exchange
        // wallet (deposit/hot/cold/…); is_offramp_point = this address
        // deposited into a CEX (the KYC/subpoena target). Read by
        // NodeIdentityChips for the wallet-role + off-ramp chips.
        wallet_role?: string | null;
        label_class?: string | null;
        brand?: string | null;
        confirmation_state?: string | null;
        source?: string | null;
        attribution_method?: string | null;
        attribution_tier?: string | null;
        is_offramp_point?: boolean;
        offramp_exchanges?: string[];
        offramp_wallet_role?: string | null;
    };
    /** Canvas-bound label list. Mirrored to node.data.node_labels. */
    node_labels?: string[];
    /** Canvas-bound tag list. Mirrored to node.data.entity_tags. */
    entity_tags?: string[];
}

export interface TriageState {
    triageData: TriagePayload | null;
    triageLoading: boolean;
    triageError: string | null;

    // ── Actions ──

    /** Load triage for an address. Handles loading/error state internally. */
    loadTriage: (address: string, chain: string, options?: { fast?: boolean }) => Promise<void>;

    /**
     * Load triage with R8 stale-response guard + optional delay.
     * Returns a cleanup function that cancels the in-flight request.
     */
    loadTriageGuarded: (
        address: string,
        chain: string,
        options?: { fast?: boolean; delayMs?: number }
    ) => () => void;

    /** Clear triage data (e.g. when selecting a non-address node). */
    clearTriage: () => void;

    /** Set triage error directly. */
    setTriageError: (error: string | null) => void;

    /** Patch entity attribution on the loaded triage summary — e.g. after a
     *  force re-resolve (F-3b). No-op if the address doesn't match the loaded
     *  triage. Lets the identity chip reflect a re-resolved entity without a
     *  full triage re-run. */
    patchEntity: (address: string, fields: Record<string, unknown>) => void;
}

// ── Module-scope state for R8 guard + delay timer ──

let _requestId = 0;
let _delayTimer: ReturnType<typeof setTimeout> | null = null;

export const useTriageStore = create<TriageState>((set) => ({
    triageData: null,
    triageLoading: false,
    triageError: null,

    loadTriage: async (address, chain, options) => {
        if (!address) {
            set({ triageData: null, triageError: null, triageLoading: false });
            return;
        }

        set({ triageLoading: true, triageError: null });
        try {
            const triage = await addressService.getTriage(chain, address, 1, { fast: !!options?.fast });
            set({ triageData: triage as TriagePayload });

            // UX hints
            const txCount = triage?.summary?.tx_count ?? (triage as any)?.details?.tx_count;
            const isContract = (triage as any)?.details?.is_contract;
            if (isContract) {
                toast('This appears to be a token contract address.', { duration: 4000, icon: 'ℹ️' });
            } else if (txCount === 0 && ['ETH', 'MATIC', 'BNB', 'ARB', 'BASE'].includes(chain)) {
                toast('No activity on ' + chain + '. Try another EVM chain via the chain selector.', { duration: 5000, icon: '💡' });
            }
        } catch (e: any) {
            console.error('Failed to load triage', e);
            set({
                triageError: toErrorMessage(e, 'Triage unavailable'),
                triageData: null,
            });
        } finally {
            set({ triageLoading: false });
        }
    },

    loadTriageGuarded: (address, chain, options) => {
        // Cancel any pending delayed load
        if (_delayTimer) {
            clearTimeout(_delayTimer);
            _delayTimer = null;
        }

        // Increment request ID for R8 stale-response guard
        const myRequestId = ++_requestId;
        let cancelled = false;

        const run = async () => {
            if (cancelled) return;
            set({ triageLoading: true, triageError: null });

            try {
                const triage = await addressService.getTriage(chain, address, 1, { fast: !!options?.fast });
                if (cancelled || _requestId !== myRequestId) return; // R8: stale
                set({ triageData: triage as TriagePayload });

                // UX hints
                const txCount = triage?.summary?.tx_count ?? (triage as any)?.details?.tx_count;
                const isContract = (triage as any)?.details?.is_contract;
                if (isContract) {
                    toast('This appears to be a token contract address.', { duration: 4000, icon: 'ℹ️' });
                } else if (txCount === 0 && ['ETH', 'MATIC', 'BNB', 'ARB', 'BASE'].includes(chain)) {
                    toast('No activity on ' + chain + '. Try another EVM chain via the chain selector.', { duration: 5000, icon: '💡' });
                }
            } catch (e: any) {
                if (cancelled || _requestId !== myRequestId) return;
                console.error('Failed to load triage', e);
                set({
                    triageError: toErrorMessage(e, 'Triage unavailable'),
                    triageData: null,
                });
            } finally {
                if (!cancelled && _requestId === myRequestId) {
                    set({ triageLoading: false });
                }
            }
        };

        const delayMs = options?.delayMs ?? 0;
        if (delayMs > 0) {
            _delayTimer = setTimeout(run, delayMs);
        } else {
            run();
        }

        // Return cleanup function (R8: cancels this specific request)
        return () => {
            cancelled = true;
            if (_delayTimer) {
                clearTimeout(_delayTimer);
                _delayTimer = null;
            }
        };
    },

    clearTriage: () =>
        set({ triageData: null, triageLoading: false, triageError: null }),

    setTriageError: (error) => set({ triageError: error }),

    patchEntity: (address, fields) => set((state) => {
        const td: any = state.triageData;
        if (!td?.summary) return {} as any;
        const tdAddr = (td.address || td.summary.address || '').toLowerCase();
        if (tdAddr && tdAddr !== (address || '').toLowerCase()) return {} as any;
        return { triageData: { ...td, summary: { ...td.summary, ...fields } } };
    }),
}));

// ── Derived value helpers ──
// These read from both useTriageStore and useSelectionStore at call-time.
// Use in components: const riskScore = getTriageRiskScore();

import { useSelectionStore } from './useSelectionStore';

/** Check if current triageData matches the selected node. */
export function getIsTriageMatch(): boolean {
    const { triageData } = useTriageStore.getState();
    const { selectedNode } = useSelectionStore.getState();
    if (!selectedNode?.id || !triageData?.address) return false;
    return selectedNode.id.toLowerCase() === triageData.address.toLowerCase();
}

/** Get triage risk score (only if triage matches selected node). */
export function getTriageRiskScore(): number | undefined {
    if (!getIsTriageMatch()) return undefined;
    const { triageData } = useTriageStore.getState();
    return typeof triageData?.summary?.risk_score === 'number'
        ? triageData.summary.risk_score
        : undefined;
}

// Removed (A3 phase 1, 2026-06-14): getDisplayedRiskScore (the `max(node, triage)`
// reconciliation smell), getTriageEntityName/Type/Source. All four were exported
// and imported but never called — vestigial read-time reconciliation that the
// per-node-keyed record.intel (A3 phase 2) replaces outright. getTriageRiskScore
// and getIsTriageMatch are kept: both still have real consumers in NodeInspector.
